/**
 * Desktop Audio Recorder Bridge
 *
 * Provides a type-safe interface to the Tauri audio-recorder plugin commands.
 * Follows the same pattern as desktopAuthBridge for consistency.
 *
 * @see tauri-plugin-audio-recorder/src/commands.rs
 */

import { isTauri } from '../utils';

import type { AvailableDevices, PermissionStatus, StartIdleStreamOptions } from './types';

// Re-export types for convenience
export type {
  AudioDevice,
  AudioLevels,
  AudioLevelsPayload,
  AvailableDevices,
  PermissionStatus,
  StartIdleStreamOptions,
} from './types';

// Re-export permission utilities
export { checkMicrophonePermissionSilent, PERMISSION_MESSAGES } from './permissions';

// Re-export device utilities
export {
  DEVICE_MESSAGES,
  ensureAvailableDevices,
  findDefaultDevice,
  findDefaultOutputDevice,
  getAvailableDevices,
  resolveDeviceId,
  resolveOutputDeviceId,
} from './devices';

const COMMAND_PREFIX = 'plugin:audio-recorder|';

/**
 * Invoke an audio-recorder plugin command with type safety
 */
async function invokeAudioRecorderCommand<T>(
  command: string,
  payload?: Record<string, unknown>
): Promise<T> {
  if (!isTauri()) {
    throw new Error(
      'Desktop Audio Recorder bridge is only available inside the Tauri runtime'
    );
  }

  const { invoke } = await import('@tauri-apps/api/core');
  return invoke<T>(`${COMMAND_PREFIX}${command}`, payload ?? {});
}

/**
 * Desktop Audio Recorder Bridge
 *
 * Provides methods to interact with the audio-recorder Tauri plugin.
 * All methods are async and throw errors if not in Tauri runtime.
 */
export const desktopAudioRecorderBridge = {
  /**
   * Check if the bridge is available (running in Tauri)
   */
  isAvailable(): boolean {
    return isTauri();
  },

  /**
   * Get all available audio devices (both input and output)
   *
   * @returns AvailableDevices with input_devices and output_devices arrays
   * @throws Error if device enumeration fails
   */
  async getAvailableDevices(): Promise<AvailableDevices> {
    return invokeAudioRecorderCommand<AvailableDevices>('get_available_devices');
  },

  /**
   * Start the idle audio visualization stream
   *
   * @param options - Optional configuration (deviceId)
   * @throws Error if stream is already running or device not found
   */
  async startIdleStream(options: StartIdleStreamOptions = {}): Promise<void> {
    return invokeAudioRecorderCommand<void>('start_idle_stream', {
      deviceId: options.deviceId ?? null,
    });
  },

  /**
   * Stop the idle audio visualization stream
   *
   * @throws Error if stream fails to stop gracefully
   */
  async stopIdleStream(): Promise<void> {
    return invokeAudioRecorderCommand<void>('stop_idle_stream');
  },

  /**
   * Read the microphone permission status without prompting
   *
   * Preserves the distinction between "denied" and "never asked" so callers that must
   * not trigger the OS permission prompt can stay off the microphone. A caller that
   * wants the permissive reading writes `status !== 'Denied'`.
   */
  async checkMicrophonePermissionStatus(): Promise<PermissionStatus> {
    return invokeAudioRecorderCommand<PermissionStatus>(
      'check_microphone_permission_status'
    );
  },
};
